import { CodewordType } from "@phonero/common-graphql/types"
import {
  ExtraDataPackageSingleSubscriptionFragment,
  ExtraDataPackagesSingleSubscriptionDetailsFragment,
} from "../queries"
import { CheckoutProductHeader } from "./CheckoutProductHeader"
import { Markdown, useAppTranslations } from "../util"
import { PuxText } from "@phonero/pux-react"
import css from "./AddDataProductHeader.module.scss"
import { DataBoostTheme, DataPackageTheme, useCampaignTheme } from "../Themes"
import { ShowLessOrAll } from "../Other"

export function AddDataProductHeader({
  product,
}: {
  product: Pick<
    (ExtraDataPackagesSingleSubscriptionDetailsFragment &
      ExtraDataPackageSingleSubscriptionFragment),
    | "productId"
    | "theme"
    | "id"
    | "price"
    | "minPrice"
    | "maxPrice"
    | "size"
    | "name"
    | "sizeType"
    | "type"
    | "roamingDurationDays"
    | "countries"
    | "campaignExpiryDate"
    | "campaign"
    | "vippsOnly"
    | "availableOnSubscriptionsCount"
  >
}) {
  const { t, i18n } = useAppTranslations()
  const isCampaignProduct = !!product?.theme

  const theme = useCampaignTheme(product.theme, product.type)

  const isDataPackageCampaign =
    theme?.type === CodewordType.Extradataeea ||
    theme?.type === CodewordType.Extraboost

  const dataTheme = isDataPackageCampaign
    ? (theme as DataPackageTheme)
    : undefined

  const boostTheme =
    theme && !isDataPackageCampaign ? (theme as DataBoostTheme) : undefined

  const getProductTitleForBoostProduct = (
    size: number | undefined,
    productType: CodewordType | undefined,
    sizeType: string | undefined | null
  ) => (
    <>
      <div style={{ fontSize: "5rem", marginRight: 10 }}>{product.size}</div>
      <div style={{ fontWeight: "normal" }}>
        {t("ExtraData.SizeType", {
          context: sizeType!,
          count: product.size!,
        })}{" "}
        <span style={{ textTransform: "lowercase" }}>
          {t("ExtraData.UnlimitedData", { context: productType?.toString() })}
        </span>
      </div>
    </>
  )

  function getProductDescription(
    product?: Pick<
      ExtraDataPackagesSingleSubscriptionDetailsFragment &
        ExtraDataPackageSingleSubscriptionFragment,
      "description" | "size" | "type" | "sizeType" | "roamingDurationDays"
    >
  ) {
    if (!!product?.description) {
      return <Markdown>{product?.description}</Markdown>
    } else if (!!dataTheme?.description) {
      return <Markdown>{dataTheme.description}</Markdown>
    } else {
      return t("ExtraData.ProductDescription", {
        size: product?.size,
        ...(product?.type !== undefined && {
          context: `${product.type}${
            !!product.roamingDurationDays ? "_HIDE_DURATION" : ""
          }${
            product.type === CodewordType.Speedboost
              ? "_" + product.sizeType
              : ""
          }`,
        }),
      })
    }
  }

  return (
    <CheckoutProductHeader
      title={
        {
          [CodewordType.Extradatainternational]: `${product.name} (${product.size} ${product.sizeType})`,
          [CodewordType.Extradataeea]: `${product.size} ${product.sizeType} Ekstra Data`,
          [CodewordType.Extraboost]: getProductTitleForBoostProduct(
            product.size!,
            product.type,
            product.sizeType
          ),
          [CodewordType.Speedboost]: getProductTitleForBoostProduct(
            product.size!,
            product.type,
            product.sizeType
          ),
        }[product.type!] || product.name
      }
      {...(isDataPackageCampaign && {
        title: `${product.size} ${t("ExtraData.SizeType", {
          context: product.sizeType!,
          count: product.size!,
        })} ${product.name}`,
      })}
      {...((product.type === CodewordType.Extraboost ||
        isDataPackageCampaign) && {
        variant: "boxed",
      })}
      {...((product.type === CodewordType.Extraboost ||
        product.type === CodewordType.Speedboost) && {
        variant: "boxed",
      })}
      priceExVat={product.price ?? product?.minPrice ?? undefined}
      isPriceFrom={product.minPrice !== product.maxPrice}
      dataTheme={dataTheme}
      boostTheme={boostTheme}
      descriptions={[
        <>
          {product.type === CodewordType.Extradatainternational && (
            <div>
              {t("ExtraData.Included", {
                count: product.size!,
                sizeType: product.sizeType!,
              })}
            </div>
          )}
          {product.roamingDurationDays && (
            <div>
              {t("ExtraData.DurationDays", {
                context: product.roamingDurationDays !== 1 ? "plural" : "",
                count: product.roamingDurationDays,
              })}
            </div>
          )}
          <div className={css.richDescription}>
            {getProductDescription(product)}
          </div>
          {product?.type === CodewordType.Extradataeea && (
            <div>
              <br />
              <PuxText>{t("ExtraData.NoSatShip")}</PuxText>
            </div>
          )}
        </>,
        !!product.countries && product.countries?.length && (
          <>
            <em>{t("ExtraData.Countries")}</em>
            <ShowLessOrAll
              items={
                product.countries?.filter((c) => !!c).map((c) => c as string) ??
                []
              }
            />
          </>
        ),
      ]}
    />
  )
}
